<script>
import { mapState, mapActions } from 'vuex';
import store from '../../../store';
import validatorMixin from '../../../mixins/validatorMixin';
import Vue from 'vue';
import TwCopyAndTranslateModal from '../../../components/TwCopyAndTranslateModal';

export default {
  components: { TwCopyAndTranslateModal },
  mixins: [ validatorMixin ],
  data() {
    return {
      fab: false,
      formValid: true,
      saveSuccess: false,
      snackbar: false,
      changed: false,
      copyAndTranslateModalOpen: false,
    };
  },
  computed: {
    ...mapState({
      active: state => state.areas.snowGuarantee.active,
      description: state => state.areas.snowGuarantee.description,
      title: state => state.areas.snowGuarantee.title,
      domains: state => state.areas.snowGuarantee.domains,
      loading: state => state.areas.snowGuarantee.loading,
      error: state => state.areas.snowGuarantee.error,
      area: state => state.areas.area,
      selectedDomainId: state => state.ski.selectedDomainId,
      domainsList: state => state.twenty.domains.items,
    }),
    getCopyValues() {
      return [
        {
          globalModelId: this.area.id,
          globalModelColumnName: 'ski_snow_guarantee_area_id',
          selectKeyOverrides: [ 'domain_id', 'ski_snow_guarantee_area_id' ],
          tableName: 'domain_ski_snow_guarantee',
          sourceDomainId: null,
          selectedFromDomain: null,
          fromLanguageIsoOverride: null,
          sourceIsoOverride: null,
          disableSourceIsoOverride: true,
          toDomains: [],
          shouldTranslate: false,
          shouldCopySeo: false,
          fieldNamesAndValuesToCopyAndTranslate: [
            { title: this.title },
            { description: this.description },
            { domain_id: null },
            { ski_snow_guarantee_area_id: this.area.id },
          ],
          title: `Snow guarantee: ${this.title}`,
        },
      ];
    },
  },
  watch: {
    selectedDomainId() {
      this.handleDomains();
    },
  },
  mounted() {
    this.$refs.snowGuaranteeForm.validate();
    this.handleDomains();
  },
  methods: {
    ...mapActions({
      setSnowGuarantee: 'areas/setSnowGuarantee',
      updateSnowGuarantee: 'areas/updateSnowGuarantee',
    }),
    onChange(payload) {
      this.setSnowGuarantee(payload);
      this.changed = true;
      this.$nextTick(() => {
        this.$refs.snowGuaranteeForm.validate();
      });
    },
    save() {
      if (this.$refs.snowGuaranteeForm.validate()) {
        this.updateSnowGuarantee({
          id: this.area.id,
          payload: {
            title: this.title && this.title.length ? this.title : null,
            active: this.active,
            description: this.description && this.description.length ? this.description : null,
            domains: this.domains,
          },
        }).then(() => {
          this.saveSuccess = true;
          this.snackbar = true;
          this.changed = false;
        }).catch(() => {
          this.saveSuccess = false;
          this.snackbar = true;
        });
      }
    },
    handleDomains() {
      if (this.selectedDomainId !== null) {
        if (!this.domains[this.selectedDomainId]) {
          Vue.set(this.domains, this.selectedDomainId, {
            domainId: this.selectedDomainId,
            title: '',
            description: '',
          });
        }
      }
    },
    updateDomainDescription(description) {
      this.domains[this.selectedDomainId].description = description.description;
    },
  },
  beforeRouteEnter(to, from, next) {
    store.dispatch('areas/fetchSnowGuarantee', to.params.id)
      .then(() => {
        next();
      })
      .catch(() => {
        next();
      });
  },
  beforeRouteLeave(to, from, next) {
    return this.$changeDetect(this.changed, next);
  },
};
</script>
<template>
  <div class="ski-area-snow-guarantee">
    <v-form ref="snowGuaranteeForm" v-model="formValid">
      <div class="py-4 px-4">
        <div class="title mb-3">
          {{ $t('snowGuarantee.headings.active') }}
        </div>
        <div class="body-1 mb-3">
          {{ $t('snowGuarantee.labelDescriptions.active') }}
        </div>
        <v-checkbox
          :label="$t('snowGuarantee.labels.active')"
          :disabled="loading"
          :input-value="active"
          :rules="[rules.visibility.bind(this, { title: title, description: description })]"
          @change="onChange({ active: $event })"
        />
      </div>
      <v-btn
        :loading="loading"
        color="primary success"
        @click="copyAndTranslateModalOpen = !copyAndTranslateModalOpen"
      >
        Copy/translate
      </v-btn>
      <tw-copy-and-translate-modal
        v-if="copyAndTranslateModalOpen"
        :elements-to-handle="getCopyValues"
        :domains="domainsList"
      />
      <v-divider/>
      <div class="py-4 px-4">
        <div class="title mb-3">
          {{ $t('snowGuarantee.headings.title') }}
        </div>
        <div class="body-1 mb-3">
          {{ $t('snowGuarantee.labelDescriptions.title') }}
        </div>
        <v-text-field
          :disabled="loading"
          :value="title"
          :rules="[rules.title]"
          label="Global Title"
          @input="onChange({ title: $event })"
        />
        <v-text-field
          v-if="selectedDomainId !== null"
          v-model="domains[selectedDomainId].title"
          :disabled="loading"
          :rules="[rules.title]"
          label="Domain Title"
        />
      </div>
      <v-divider/>
      <div class="py-4 px-4">
        <div class="title mb-3">
          Global Description
        </div>
        <div class="body-1 mb-3">
          {{ $t('snowGuarantee.labelDescriptions.description') }}
        </div>
        <tw-wysiwyg
          :value="description"
          :disabled="loading"
          placeholder="Global Description"
          @change="onChange({ description: $event })"
        />
        <div v-if="selectedDomainId !== null" class="title mb-3 mt-3">
          Domain Description
        </div>
        <div v-if="selectedDomainId !== null" class="body-1 mb-3">
          {{ $t('snowGuarantee.labelDescriptions.description') }}
        </div>
        <tw-wysiwyg
          v-if="selectedDomainId !== null"
          :value="domains[selectedDomainId].description"
          :disabled="loading"
          placeholder="Domain Description"
          @change="updateDomainDescription({ description: $event })"
        />
      </div>
      <v-tooltip :disabled="formValid" left>
        <template v-slot:activator="{ on }">
          <v-btn
            :loading="loading"
            fixed
            bottom
            right
            color="primary"
            class="ski-area-snow-guarantee__button"
            v-on="on"
            @click="save"
          >
            {{ $t('save') }}
          </v-btn>
        </template>
        <span v-if="!formValid">{{ $t('formInvalid') }}</span>
      </v-tooltip>
    </v-form>
    <v-snackbar
      v-model="snackbar"
      :timeout="4000"
      bottom
    >
      <v-icon v-if="saveSuccess" class="mr-3 green--text">check_circle</v-icon>
      <v-icon v-else class="mr-3 red--text">warning</v-icon>
      {{ saveSuccess
        ? $t('snowGuarantee.snackbar.save.success')
        : $t('snowGuarantee.snackbar.save.error')
      }}
      <v-btn
        color="white"
        flat
        @click="snackbar = false"
      >
        {{ $t('snowGuarantee.snackbar.save.button') }}
      </v-btn>
    </v-snackbar>
  </div>
</template>

<style lang="scss">
  .ski-area-snow-guarantee {
    &__button {
      z-index: 1;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/ski/areas/SkiAreaSnowGuarantee.vue